import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "reactstrap";
import { useFetch2 } from "components/utils/authenticatedHook";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import LottieLoader from "components/LottieLoader";
import lottieLoaderFile from "files/animation/loading.json";
import lottieEmptyFile from "files/animation/empty_new.json";
import moment from "moment";
import VitalsIcon from "files/vitals.jpg";
import _ from "lodash";
import { pasteValue } from "../../Reusable/OnPasteValue";
import AdminExtremeExceptionsDetails from "./AdminExtremeExceptionsDetails";
import hypertensionImages from "files/blood-pressure.png";

const AdminExtremeExpectations = ({ careProviderId, isActive = false }) => {
  const [{ response, isLoading }, doFetch] = useFetch2();
  const { data = [], limit = 0, page = 0, total = 0 } = response || {};
  const [search, setSearch] = useState("");
  const normalizedCareProviderId =
    typeof careProviderId === "string" ? careProviderId.trim() : careProviderId;

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {"   "}Showing {from} to {to} of {size} Results
    </span>
  );

  const options = {
    page: parseInt(page || 0),
    sizePerPage: parseInt(limit || 0),
    totalSize: parseInt(total || 0),
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    nextPageTitle: "First page",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    hideSizePerPage: parseInt(total) == 0 ? true : false,
    hidePageListOnlyOnePage: true,
    showTotal: parseInt(total) == 0 ? false : true,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "200", value: 200 },
    ],
  };

  useEffect(() => {
    if (!isActive) return;
    if (!normalizedCareProviderId) return;
    getExtremeExpectations();
  }, [isActive, normalizedCareProviderId]);

  function getExtremeExpectations(filter) {
    const query = {
      page: filter?.page ?? 1,
      limit: filter?.limit ?? 25,
      sortOrder: filter?.sortOrder ?? "desc",
      sortFiled: filter?.sortFiled ?? "createdAt",
    };
    if ((filter && filter?.search) || search) {
      query.search = filter?.search || search;
    }
    if (normalizedCareProviderId) {
      query.careProviderId = normalizedCareProviderId;
    }
    doFetch({
      url: normalizedCareProviderId ? `/get-extreme-reading-expectations/${normalizedCareProviderId}` : "/get-extreme-reading-expectations",
      method: "GET",
      disableWait: true,
      query,
    });
  }

  function reloadData(event) {
    if (event && isActive) {
      getExtremeExpectations();
    }
  }

  function pagination(type, { page, sizePerPage, sortField, sortOrder }) {
    if (!isActive) return;
    let tableData = {
      page,
      limit: sizePerPage,
    };
    if (sortField) tableData.sortFiled = sortField;
    if (sortOrder) tableData.sortOrder = sortOrder;
    getExtremeExpectations(tableData);
  }

  function withIcon(cell) {
    let icon;
    switch (cell) {
      case "Vitals":
        icon = VitalsIcon;
        break;
      case "Event Expectation":
        icon = hypertensionImages;
        break;
      default:
        break;
    }
    return (
      <span>
        <img src={icon} className="mr-2" alt="image" />
        {cell == "Weight" ? "Weight Difference" : cell}
      </span>
    );
  }

  const debounceTimeout = useRef();
  function handleSearch(event) {
    if (!isActive) return;
    const value = event.target.value;
    setSearch(value);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      getExtremeExpectations({ search: value });
    }, 800);
  }

  return (
    <div>
      <div className="admin-table-toolbar mb-2">
        <div className="admin-table-toolbar-search">
          <div className="admin-search-wrapper">
            <i className="mdi mdi-magnify admin-search-icon" />
            <input
              type="text"
              className="form-control input-date-filter remove-bottom-margin admin-search-input"
              id="exampleInputName1Test"
              placeholder="Search expectation name, message..."
              onChange={handleSearch}
              onPaste={(e) => {
                e.preventDefault();
                const pasteEvent = pasteValue(e);
                handleSearch(pasteEvent);
              }}
            />
          </div>
        </div>
      </div>
      <div className="scrollbar-table-cls">
        <BootstrapTable
          remote={{
            sort: false,
            pagination: true,
            filter: true,
            search: true,
          }}
          wrapperClasses="table-responsive"
          keyField="_id"
          noDataIndication={() => {
            return (
              <div>
                {isLoading && (
                  <LottieLoader
                    loadLoop={true}
                    autoplayLoop={true}
                    source={lottieLoaderFile}
                    Lheight={150}
                    Lwidth={150}
                    subTitle={"Please Wait ..."}
                  />
                )}
                {!isLoading && (
                  <LottieLoader
                    loadLoop={true}
                    autoplayLoop={true}
                    source={lottieEmptyFile}
                    Lheight={300}
                    Lwidth={300}
                    title={"Sorry! No data found"}
                    subTitle={"We can't find any event expectation - retest logic data."}
                  />
                )}
              </div>
            );
          }}
          data={isLoading ? [] : data}
          columns={[
            {
              dataField: "name",
              text: "Name",
              sort: true,
              formatter: (cell) => <div>{cell ? cell : "-"}</div>,
            },
            {
              dataField: "ruleType",
              text: "Expectation Type",
              sort: true,
              formatter: withIcon,
            },
            {
              dataField: "readingType",
              text: "Reading Type",
              sort: true,
              formatter: (cell) => <div>{cell ? cell.description : "-"}</div>,
            },
            {
              dataField: "condition",
              text: "Expectation",
              sort: false,
              formatter: (cell, row) => {
                if (row.readingType && row.readingType._id == "5bb306382598931ffbd1b625") {
                  const weight = row.valueProperty && row.valueProperty[0].gainLossPerDay ? row.valueProperty[0].gainLossPerDay : "";
                  return `${weight} Per Day`;
                } else {
                  if (cell == "<=") return "Less than or equal to";
                  if (cell == ">=") return "Greater than or equal to";
                  if (cell == "between") return "Between";
                }
              },
            },
            {
              dataField: "valueProperty",
              text: "Value",
              sort: false,
              formatter: (cell, row) => {
                if (Array.isArray(cell) && cell.length > 0 && row.readingType && row.readingType._id !== "5bb306382598931ffbd1b625") {
                  return (
                    <div>
                      {cell.map((item, idx) => (
                        <div key={item.readingSubTypeId || idx} style={{ display: "block", lineHeight: "1.3" }}>
                          <strong>{item.name === "pulseRate" ? "Heart Rate" : item.name}:</strong>{" "}
                          {item.value || item.minValue || item.maxValue
                            ? [item.value && `${item.value}`, item.minValue && `Min: ${item.minValue}`, item.maxValue && `Max: ${item.maxValue}`]
                              .filter(Boolean)
                              .join(" | ")
                            : "-"}
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  return <div>-</div>;
                }
              }
            },
            {
              dataField: "notifySelf",
              text: "Notification",
              sort: false,
              headerStyle: () => ({ width: "320px", textAlign: "center", padding: "16px", fontSize: "16px", color: "#070707" }),
              formatter: (cell, row) => {
                if ((row.notifyReadingOwner && row.notifyReadingOwner.length > 0) || (row.notifySelf && row.notifySelf.length) > 0) {
                  return (
                    <div className="text-center">
                      <table style={{ width: "100%" }}>
                        <tr>
                          <th className="text-center">CareProvider</th>
                          <th className="text-center">Staff</th>
                          <th className="text-center">Patient</th>
                        </tr>
                        <tr>
                          <td>
                            {row.notifySelf && row.notifySelf.length > 0 ? <div className="text-center">{_.map(_.uniqBy(row.notifySelf), (v) => _.startCase(v)).toString()} </div> : <div className="text-center"> - </div>}
                          </td>
                          <td>
                            {row.notifyStaff && row.notifyStaff.length > 0 ? <div className="text-center">{_.map(_.uniqBy(row.notifyStaff), (v) => _.startCase(v)).toString()} </div> : <div className="text-center"> - </div>}
                          </td>
                          <td>
                            {row.notifyReadingOwner && row.notifyReadingOwner.length > 0 ? <div className="text-center">{_.map(_.uniqBy(row.notifyReadingOwner), (v) => _.startCase(v)).toString()} </div> : <div className="text-center"> - </div>}
                          </td>
                        </tr>
                      </table>
                    </div>
                  );
                } else {
                  return <div className="text-center">-</div>;
                }
              }
            },
            {
              dataField: "updatedAt",
              text: "Last Updated",
              sort: true,
              formatter: (cell, row) => row.updatedAt ? moment(row.updatedAt).tz(moment.tz.guess()).format("MMMM DD, YYYY") : "NONE",
              headerStyle: () => ({ padding: "16px", fontSize: "16px", color: "#070707" }),
            }
          ]}
          pagination={options.page > 0 ? paginationFactory(options) : undefined}
          bordered
          onTableChange={pagination}
          expandRow={{
            renderer: (row) => (
              <div style={{ margin: 15 }}>
                <AdminExtremeExceptionsDetails
                  details={row}
                  reload={reloadData}
                  careProviderId={normalizedCareProviderId}
                />
              </div>
            ),
            onlyOneExpanding: false,
            showExpandColumn: true,
            expandByColumnOnly: false,
          }}
        />
      </div>
    </div>
  );
};

export default AdminExtremeExpectations;
